export function formatTimeISO(dateStr: string | Date, locale = 'it-IT') {
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

export function formatDayLabel(dateStr: string | Date, locale = 'it-IT') {
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isToday) return 'Oggi';
  if (isYesterday) return 'Ieri';
  return d.toLocaleDateString(locale, { weekday: 'long', day: '2-digit', month: 'short' });
}

const monthBadgeFormatter = new Intl.DateTimeFormat('it-IT', { month: 'short' });

export function formatMonthBadge(date: Date) {
  return monthBadgeFormatter.format(date).replace('.', '').toUpperCase();
}

export function getCalendarBadgeParts(value?: string | Date | null) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    day: `${date.getDate()}`.padStart(2, '0'),
    month: formatMonthBadge(date),
  };
}

export type DayGroup<T> = {
  key: string;
  date: Date | null;
  items: T[];
};

export function groupItemsByDay<T>(
  items: T[],
  getTimestamp: (item: T) => string | Date | null | undefined,
  unknownKey = 'sconosciuto',
): DayGroup<T>[] {
  const map = new Map<string, { date: Date | null; items: T[] }>();
  const getTimestampValue = (item: T) => {
    const timestamp = getTimestamp(item);
    if (!timestamp) return 0;
    const parsed = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  };

  items.forEach((item) => {
    const timestamp = getTimestamp(item);
    const parsed = timestamp instanceof Date ? timestamp : timestamp ? new Date(timestamp) : null;
    const isValid = !!parsed && !Number.isNaN(parsed.getTime());
    const dayKey = isValid ? parsed.toDateString() : unknownKey;
    const group = map.get(dayKey) || { date: isValid ? parsed : null, items: [] };
    group.items.push(item);
    if (!group.date && isValid) group.date = parsed;
    map.set(dayKey, group);
  });

  return Array.from(map.entries())
    .map(([key, group]) => ({
      key,
      date: group.date,
      items: group.items.sort((a, b) => getTimestampValue(b) - getTimestampValue(a)),
    }))
    .sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return b.date.getTime() - a.date.getTime();
    });
}

export function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function formatDateTime(dateStr?: string | Date | null, locale = 'it-IT') {
  if (!dateStr) return '—';
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
